import React from "react";
import { Loader2, Search, X } from "lucide-react";
import type { Mcc } from "../../../features/purposeCode/PurposeCodeSlice";

interface MccListTabProps {
  loading: boolean;
  error: string | null;
  search: string;
  selectedMcc: string;
  detailLoading: boolean;
  totalCount: number;
  rows: Mcc[];
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onMccClick: (mcc: string) => void;
}

const MccListTab: React.FC<MccListTabProps> = ({
  loading,
  error,
  search,
  selectedMcc,
  detailLoading,
  totalCount,
  rows,
  onSearchChange,
  onClearSearch,
  onMccClick,
}) => {
  const isInitialLoading = loading && totalCount === 0;
  const isRefreshing = loading && totalCount > 0;

  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border-[1px] border-cardborder">
      <div className="relative mb-5">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by MCC code or description"
          className="w-full pl-9 pr-10 py-2.5 text-sm border border-gray-300 rounded-md focus:outline-none"
        />
        {!!search && (
          <button
            type="button"
            onClick={onClearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="text-xs text-gray-500 mb-4">
        Showing {rows.length} of {totalCount} MCC codes
      </div>

      {isInitialLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading MCCs...
        </div>
      ) : error ? (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {error}
        </div>
      ) : (
        <div className="relative">
          {isRefreshing && (
            <div className="absolute right-0 -top-8 text-xs text-gray-500 inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Refreshing...
            </div>
          )}
          <div className="max-h-[460px] overflow-y-auto overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-left border-b border-gray-200 text-gray-600">
                <th className="py-3.5 px-4 font-medium">MCC</th>
                <th className="py-3.5 pr-4 font-medium">Description</th>
                <th className="py-3.5 pr-4 font-medium">Action</th>
              </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.mcc}
                    onClick={() => onMccClick(row.mcc)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onMccClick(row.mcc);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 focus:outline-none focus:bg-gray-50 ${
                      selectedMcc === row.mcc ? "bg-green-50/60" : ""
                    }`}
                  >
                    <td className="py-3.5 px-4 font-medium text-gray-800 whitespace-nowrap">{row.mcc}</td>
                    <td className="py-3.5 pr-4 text-gray-700">{row.description}</td>
                    <td className="py-3.5 pr-4">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMccClick(row.mcc);
                        }}
                        disabled={detailLoading && selectedMcc === row.mcc}
                        className="px-3 py-1.5 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      >
                        <span className="inline-flex items-center gap-1">
                          {detailLoading && selectedMcc === row.mcc && (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          )}
                          View Purpose Codes
                        </span>
                      </button>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={3} className="py-10 text-center text-gray-500">
                      {search ? "No MCC codes match your search" : "No MCCs found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MccListTab;
